/** New-tag-category dialog: tenant + code + name (+ optional reusable schema),
 *  POST, then navigate to the created category. Encourages picking a reusable
 *  schema at creation time; inline schemas are edited later on the detail page. */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as tagCategoriesApi from '@/api/tagCategories';
import type { TagCategorySchemaRef } from '@/api/tagCategories';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

import { TagSchemaPicker } from './TagSchemaPicker';

export function TagCategoryCreateDialog({
  open,
  onClose,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9) — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [systemDefault, setSystemDefault] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [schemaRef, setSchemaRef] = useState<TagCategorySchemaRef | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    resetTenant();
    setSystemDefault(false);
    setCode('');
    setName('');
    setSchemaRef(null);
    setError(null);
  };

  // Page tenants: every open starts a fresh form on the page's tenant, so a
  // Cancelled pick (and the fields that hang on it) never carries over.
  useEffect(() => {
    if (open && pageTenants !== undefined) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: (body: tagCategoriesApi.TagCategoryCreateInput) => tagCategoriesApi.create(body),
    onSuccess: (cat) => {
      snackbar.show('Category created.', 'success');
      qc.invalidateQueries({ queryKey: ['tag_categories'] });
      reset();
      onClose();
      navigate(`/tag-categories/${cat.id}`);
    },
    onError: (e) => setError(e instanceof ConsoleApiError ? e.message : 'Create failed.'),
  });

  const canSubmit =
    (systemDefault || tenant !== null) && code.trim().length > 0 && name.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New tag category</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={<Switch checked={systemDefault} onChange={(e) => { setSystemDefault(e.target.checked); if (e.target.checked) setTenant(null); }} />}
            label="System default (no tenant)"
          />
          {!systemDefault && (
            <TenantPicker value={tenant} featured={pageTenants} onChange={setTenant} label="Operator" placeholder="Select operator" />
          )}
          <TextField label="Code" size="small" fullWidth required value={code}
            onChange={(e) => setCode(e.target.value)} inputProps={{ maxLength: 64 }}
            helperText="Machine code, unique per tenant." />
          <TextField label="Name" size="small" fullWidth required value={name}
            onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 1024 }} />
          <TagSchemaPicker value={schemaRef} onChange={setSchemaRef} label="Reusable schema (recommended)" />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained" disabled={!canSubmit}
          onClick={() =>
            mutation.mutate({
              tour_operator: systemDefault ? null : (tenant?.id ?? null),
              code: code.trim(),
              name: name.trim(),
              schema_ref: schemaRef?.id ?? null,
            })
          }
        >
          {mutation.isPending ? 'Creating…' : 'Create category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
